import { HardHat, MapPin, CheckCircle, Calendar, Shield, Building } from 'lucide-react'

interface Vendor {
  id: string
  companyName: string
  locationCity: string
  locationState: string
  avatar: string | null
  completedJobs: number
  memberSince: string
  // Government vendor fields
  isGovCertified?: boolean
  cageCode?: string
  dunsNumber?: string
  gsaScheduleId?: string
  certifications?: string[]
  approvedAgencies?: string[]
  govContractsCompleted?: number
}

interface VendorCardProps {
  vendor: Vendor
  onViewDetails?: (vendorId: string) => void
  onRequestQuote?: (vendorId: string) => void
}

export default function VendorCard({ vendor, onViewDetails, onRequestQuote }: VendorCardProps) {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short' 
    })
  }

  const getTopCertifications = (certifications?: string[]) => {
    if (!certifications || certifications.length === 0) return []
    return certifications.slice(0, 2) // Show first 2 certifications
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200">
      <div className="p-6">
        {/* Company Info with Government Badge */}
        <div className="flex items-start space-x-4 mb-4">
          <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
            {vendor.avatar ? (
              <img
                src={vendor.avatar}
                alt={vendor.companyName}
                className="w-12 h-12 rounded-lg object-cover"
              />
            ) : (
              <HardHat className="h-6 w-6 text-primary-600" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-2 mb-1">
              <h3 className="text-lg font-semibold text-gray-900 truncate">
                {vendor.companyName}
              </h3>
              {vendor.isGovCertified && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                  <Shield className="w-3 h-3 mr-1" />
                  Gov Certified
                </span>
              )}
            </div>
            <div className="flex items-center text-sm text-gray-600">
              <MapPin className="h-4 w-4 mr-1 flex-shrink-0" />
              <span className="truncate">
                {vendor.locationCity}, {vendor.locationState}
              </span>
            </div>
          </div>
        </div>

        {/* Government Information */}
        {vendor.isGovCertified && (
          <div className="mb-4 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
            <div className="space-y-2">
              {vendor.cageCode && (
                <div className="flex items-center text-sm">
                  <Building className="h-4 w-4 text-emerald-600 mr-2" />
                  <span className="text-gray-700">
                    <span className="font-medium">CAGE:</span> {vendor.cageCode}
                  </span>
                </div>
              )}
              {vendor.govContractsCompleted && vendor.govContractsCompleted > 0 && (
                <div className="flex items-center text-sm">
                  <CheckCircle className="h-4 w-4 text-emerald-600 mr-2" />
                  <span className="text-gray-700">
                    <span className="font-medium">{vendor.govContractsCompleted}</span> government contracts completed
                  </span>
                </div>
              )}
              {/* Top Certifications */}
              {getTopCertifications(vendor.certifications).length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {getTopCertifications(vendor.certifications).map((cert, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2 py-1 rounded text-xs bg-emerald-100 text-emerald-700"
                    >
                      {cert}
                    </span>
                  ))}
                  {vendor.certifications && vendor.certifications.length > 2 && (
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-gray-100 text-gray-600">
                      +{vendor.certifications.length - 2} more
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Regular Stats */}
        <div className="space-y-3 mb-6">
          <div className="flex items-center text-sm">
            <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
            <span className="text-gray-600">
              <span className="font-semibold text-gray-900">{vendor.completedJobs}</span> completed jobs
            </span>
          </div>
          <div className="flex items-center text-sm">
            <Calendar className="h-4 w-4 text-gray-400 mr-2" />
            <span className="text-gray-600">
              Member since {formatDate(vendor.memberSince)}
            </span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-3">
          <button
            onClick={() => onViewDetails?.(vendor.id)}
            className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            View Details
          </button>
          <button
            onClick={() => onRequestQuote?.(vendor.id)}
            className="flex-1 px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Request Quote
          </button>
        </div>
      </div>
    </div>
  )
} 